import { useEffect } from 'react';

/**
 * Drives the scroll-reveal system in styles/motion.css.
 *
 * A single IntersectionObserver watches every `[data-reveal]` node and adds
 * `.is-in` once it enters view, then unobserves it — reveals play once, so
 * scrolling back up does not re-animate the page.
 *
 * The `js-reveal` flag on <html> is what actually hides un-revealed content,
 * so a visitor without JavaScript (or with the bundle still loading) sees the
 * page fully rendered instead of a blank column.
 */
export default function useReveal(): void {
    useEffect(() => {
        const root = document.documentElement;

        // No observer support, or the visitor asked for less motion: show
        // everything immediately and never set the hiding flag.
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced || typeof IntersectionObserver === 'undefined') {
            root.classList.remove('js-reveal');
            document
                .querySelectorAll('[data-reveal]')
                .forEach((el) => el.classList.add('is-in'));
            return;
        }

        root.classList.add('js-reveal');

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    entry.target.classList.add('is-in');
                    observer.unobserve(entry.target);
                });
            },
            // Fire a little before the element reaches the fold so the
            // animation is already settling by the time it is fully visible.
            { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
        );

        // Tracked per-effect rather than with a DOM attribute: React 18
        // StrictMode mounts, cleans up and re-mounts, and a marker left on the
        // node would make the second pass skip elements the first (now
        // disconnected) observer was watching — leaving them hidden forever.
        const seen = new WeakSet<Element>();

        const observe = (el: Element) => {
            if (seen.has(el)) return;
            seen.add(el);

            // Anything already on screen at scan time (above-the-fold content,
            // or a short page) is shown right away rather than waiting for a
            // scroll event that may never come.
            const box = el.getBoundingClientRect();
            if (box.top < window.innerHeight * 0.92 && box.bottom > 0) {
                el.classList.add('is-in');
                return;
            }
            observer.observe(el);
        };

        const scan = () => document.querySelectorAll('[data-reveal]').forEach(observe);

        scan();

        // Route changes and conditionally rendered blocks (form success
        // messages, drawers) mount new nodes after the initial scan. Coalesce
        // bursts of mutations into one scan per frame — a render that adds
        // many nodes would otherwise trigger a full-document query per node.
        let queued = 0;
        const mutations = new MutationObserver(() => {
            if (queued) return;
            queued = requestAnimationFrame(() => {
                queued = 0;
                scan();
            });
        });
        mutations.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
            mutations.disconnect();
            cancelAnimationFrame(queued);
            root.classList.remove('js-reveal');
        };
    }, []);
}
